import React, { useEffect, useState, useRef } from 'react';
import { salesApi, ebosApi, skusApi, downloadAuthed } from '../api';
import PageHeader from '../components/PageHeader';
import ImportProgressModal from '../components/ImportProgressModal';
import { Download, Upload, Plus, Loader2, X, Trash2, CheckSquare, Square, RefreshCw, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function SalesData() {
  const { user, theme } = useAuth();
  const [items, setItems] = useState([]);
  const [ebos, setEbos] = useState([]);
  const [skus, setSkus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [draft, setDraft] = useState({ eboBranchCode: '', sku: '', period: '07/2026', qty: 0 });
  const [msg, setMsg] = useState('');
  const fileRef = useRef();
  const [importState, setImportState] = useState({ open: false, phase: 'uploading', progress: 0, fileName: '', result: null, errorMsg: '' });
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncRunning, setSyncRunning] = useState(false);

  const loadSyncStatus = () => {
    salesApi.syncStatus().then(setSyncStatus).catch(() => setSyncStatus(null));
  };

  const runSyncNow = async () => {
    setSyncRunning(true);
    try {
      const run = await salesApi.syncNow();
      setMsg(run.status === 'success'
        ? `Auto sync imported ${run.written} record(s) from the Logic ERP email.`
        : `Auto sync ${run.status}: ${run.errorSummary || ''}`);
      setTimeout(() => setMsg(''), 8000);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncRunning(false);
      loadSyncStatus();
    }
  };

  const load = () => {
    setLoading(true);
    Promise.all([salesApi.list(), ebosApi.list(), skusApi.list()]).then(([s, e, k]) => {
      setItems(s); setEbos(e); setSkus(k);
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);
  useEffect(loadSyncStatus, []);

  const addNew = async () => {
    try {
      await salesApi.add({ ...draft, qty: Number(draft.qty) });
      setShowModal(false);
      setDraft({ eboBranchCode: '', sku: '', period: '07/2026', qty: 0 });
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Add failed'); }
  };

  const doImport = async (file) => {
    if (!file) return;
    setImportState({ open: true, phase: 'uploading', progress: 0, fileName: file.name, result: null, errorMsg: '' });
    try {
      const r = await salesApi.import(file, (evt) => {
        const total = evt.total || evt.loaded;
        if (total > 0) {
          setImportState(s => ({ ...s, progress: Math.round((evt.loaded / total) * 100) }));
        }
      });
      setImportState(s => ({ ...s, phase: 'done', progress: 100, result: r }));
      const errs = (r.errors || []).length;
      setMsg(errs === 0
        ? `Imported: ${r.inserted} records (${r.skipped} skipped)`
        : `Imported ${r.inserted} rows — ${errs} row${errs === 1 ? '' : 's'} skipped, see details.`);
      load();
      setTimeout(() => setMsg(''), 6000);
    } catch (e) {
      setImportState(s => ({ ...s, phase: 'error', errorMsg: e.response?.data?.detail || 'Import failed' }));
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const canUploadSales = ['SuperAdmin', 'Admin', 'AllIndiaManager', 'RetailManager'].includes(user?.role);
  const canBulkDelete = user?.role === 'SuperAdmin';

  /** iter40 — Sales periods are stored canonically as YYYY-MM but the UI
   *  (per user request) shows and accepts them as MM/YYYY. */
  const displayPeriod = (canonical) => {
    if (!canonical || typeof canonical !== 'string') return canonical || '';
    const parts = canonical.split('-');
    if (parts.length === 2 && parts[0].length === 4 && /^\d+$/.test(parts[0]) && /^\d+$/.test(parts[1])) {
      return `${parts[1].padStart(2, '0')}/${parts[0]}`;
    }
    return canonical;
  };

  // Bulk-delete selection
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleOne = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAllShown = () => setSelected(prev => {
    const ids = filteredItems.map(s => s.id).filter(Boolean);
    // If every currently-shown row is already selected, clear the shown ones.
    const allShownSelected = ids.length > 0 && ids.every(id => prev.has(id));
    const next = new Set(prev);
    if (allShownSelected) ids.forEach(id => next.delete(id));
    else ids.forEach(id => next.add(id));
    return next;
  });
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Permanently delete ${selected.size} sales record(s)? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const r = await salesApi.bulkDelete({ ids: Array.from(selected) });
      setSelected(new Set());
      setMsg(`Deleted ${r.deleted} record(s).`);
      setTimeout(() => setMsg(''), 5000);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Bulk delete failed');
    } finally { setBulkBusy(false); }
  };

  // iter37 — client-side filters (EBO / SKU / Period / Month)
  // iter43 — period filter is now a multi-select set + optional from/to range mode.
  const [fEbo, setFEbo] = useState('');
  const [fSku, setFSku] = useState('');
  const [fPeriods, setFPeriods] = useState(() => new Set());   // Set<canonical period>
  const [fMonth, setFMonth] = useState('');
  const [periodMode, setPeriodMode] = useState('list'); // 'list' | 'range'
  const [rangeFrom, setRangeFrom] = useState(''); // canonical YYYY-MM
  const [rangeTo, setRangeTo] = useState('');
  const togglePeriod = (p) => setFPeriods(prev => {
    const next = new Set(prev);
    if (next.has(p)) next.delete(p); else next.add(p);
    return next;
  });
  const filteredItems = React.useMemo(() => {
    const inRange = (canonical) => {
      if (!rangeFrom && !rangeTo) return true;
      if (rangeFrom && canonical < rangeFrom) return false;
      if (rangeTo && canonical > rangeTo) return false;
      return true;
    };
    return items.filter(s => {
      if (fEbo && s.eboBranchCode !== fEbo) return false;
      if (fSku && s.sku !== fSku) return false;
      if (periodMode === 'list' && fPeriods.size > 0 && !fPeriods.has(s.period)) return false;
      if (periodMode === 'range' && !inRange(s.period)) return false;
      // fMonth compares just the month portion "YYYY-MM"[5:7]
      if (fMonth && !(s.period || '').endsWith(`-${fMonth}`)) return false;
      return true;
    });
  }, [items, fEbo, fSku, fPeriods, fMonth, periodMode, rangeFrom, rangeTo]);
  const periodOptions = React.useMemo(() => Array.from(new Set(items.map(s => s.period).filter(Boolean))).sort(), [items]);
  const skuOptions = React.useMemo(() => Array.from(new Set(items.map(s => s.sku).filter(Boolean))).sort(), [items]);
  const hasAnyFilter = fEbo || fSku || fMonth || fPeriods.size > 0 || rangeFrom || rangeTo;
  const clearFilters = () => {
    setFEbo(''); setFSku(''); setFMonth('');
    setFPeriods(new Set());
    setRangeFrom(''); setRangeTo('');
  };

  return (
    <div>
      <PageHeader
        title="EBO sales data"
        subtitle={<span>{items.length} sales record(s) · used in Reports to compare testers issued against pieces sold</span>}
        actions={canUploadSales && (
          <div className="flex items-center gap-2">
            <button onClick={() => downloadAuthed(salesApi.templateUrl(), 'sales-data-template.xlsx')} className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#E7E2D3] bg-white text-[13px] hover:bg-[#F7F4EA]"><Download size={13}/> Download template</button>
            <input ref={fileRef} type="file" accept=".xlsx" onChange={(e) => doImport(e.target.files?.[0])} className="hidden" />
            <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#E7E2D3] bg-white text-[13px] hover:bg-[#F7F4EA]"><Upload size={13}/> Import Excel</button>
            <button onClick={() => downloadAuthed(salesApi.exportUrl(), 'sales-data.xlsx')} className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#E7E2D3] bg-white text-[13px] hover:bg-[#F7F4EA]"><Download size={13}/> Download Excel</button>
            <button onClick={() => setShowModal(true)} className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-white text-[13px]" style={{ background: theme.primary }}><Plus size={13}/> Add record</button>
            <button data-testid="sales-sync-now-btn" onClick={runSyncNow} disabled={syncRunning} className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#E7E2D3] bg-white text-[13px] hover:bg-[#F7F4EA] disabled:opacity-50">
              {syncRunning ? <Loader2 size={13} className="animate-spin"/> : <RefreshCw size={13}/>} Sync from email
            </button>
          </div>
        )}
      />

      {syncStatus?.alertActive && (
        <div data-testid="sales-sync-alert" className="mb-3 text-[13px] px-3 py-2 rounded border border-[#e5b8b0] bg-[#F9E4E0] text-[#8c2f1b] flex items-start gap-2">
          <AlertTriangle size={15} className="mt-[1px] shrink-0"/>
          <div>
            <span className="font-semibold">Automated Logic ERP sales sync failed.</span>
            {syncStatus.latest?.errorSummary && <span> {syncStatus.latest.errorSummary}.</span>}
            {' '}No rows were written. Fix the sheet or the EBO/SKU master, then use <span className="font-semibold">Sync from email</span> or wait for the next scheduled run (6 AM / 6 PM IST).
          </div>
        </div>
      )}
      {syncStatus?.lastSuccess && !syncStatus.alertActive && (
        <div data-testid="sales-sync-last-ok" className="mb-3 text-[12px] text-[#7a6f5c]">
          Auto sync active — runs daily at 6 AM &amp; 6 PM IST from the Logic ERP mailbox. Last successful import: {new Date(syncStatus.lastSuccess.finishedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} · {syncStatus.lastSuccess.written} record(s) upserted.
        </div>
      )}

      {msg && <div className="mb-3 text-[13px] px-3 py-2 rounded bg-[#E1EEE4] text-[#2f6b41]">{msg}</div>}

      <p className="text-[12px] text-[#7a6f5c] mb-3 max-w-[1100px]">
        Import matches on <span className="font-semibold text-[#232323]">EBO Branch Code (ERP) + SKU Code + Period</span> — expects columns EBO Branch Code (ERP), SKU Code, Period (MM/YYYY, e.g. <span className="font-mono">07/2026</span>), Qty Sold. Upload monthly to build a running sales history per branch — current or previous months are both accepted. Admins, All-India Managers and Retail Managers can bulk-upload sales in Excel.
      </p>

      <div className="fm-card p-3 mb-3 flex flex-wrap items-center gap-2">
        <select data-testid="sales-filter-ebo" value={fEbo} onChange={(e) => setFEbo(e.target.value)}
                className="fm-select fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13px] bg-white min-w-[180px]">
          <option value="">All EBOs</option>
          {ebos.map(e => <option key={e.code} value={e.code}>{e.name}</option>)}
        </select>
        <select data-testid="sales-filter-sku" value={fSku} onChange={(e) => setFSku(e.target.value)}
                className="fm-select fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13px] bg-white min-w-[140px]">
          <option value="">All SKUs</option>
          {skuOptions.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        {/* iter43 — Period filter: List (multi-select) OR Range (from → to). */}
        <div className="inline-flex items-center gap-1 border border-[#E7E2D3] rounded-md p-0.5 bg-white" data-testid="sales-period-mode">
          <button
            onClick={() => setPeriodMode('list')}
            className={`px-2 py-1 rounded text-[12px] ${periodMode === 'list' ? 'text-white' : 'text-[#7a6f5c]'}`}
            style={{ background: periodMode === 'list' ? theme.primary : 'transparent' }}
            data-testid="sales-period-mode-list"
          >List</button>
          <button
            onClick={() => setPeriodMode('range')}
            className={`px-2 py-1 rounded text-[12px] ${periodMode === 'range' ? 'text-white' : 'text-[#7a6f5c]'}`}
            style={{ background: periodMode === 'range' ? theme.primary : 'transparent' }}
            data-testid="sales-period-mode-range"
          >Range</button>
        </div>

        {periodMode === 'list' && (
          <PeriodMultiSelect
            options={periodOptions}
            selected={fPeriods}
            onToggle={togglePeriod}
            onClear={() => setFPeriods(new Set())}
            display={displayPeriod}
          />
        )}
        {periodMode === 'range' && (
          <div className="inline-flex items-center gap-1" data-testid="sales-period-range">
            <select value={rangeFrom} onChange={e => setRangeFrom(e.target.value)}
                    data-testid="sales-period-range-from"
                    className="fm-select fm-focus border border-[#E7E2D3] rounded-md px-2 py-2 text-[13px] bg-white min-w-[120px]">
              <option value="">From…</option>
              {periodOptions.map(p => <option key={p} value={p}>{displayPeriod(p)}</option>)}
            </select>
            <span className="text-[12px] text-[#7a6f5c]">→</span>
            <select value={rangeTo} onChange={e => setRangeTo(e.target.value)}
                    data-testid="sales-period-range-to"
                    className="fm-select fm-focus border border-[#E7E2D3] rounded-md px-2 py-2 text-[13px] bg-white min-w-[120px]">
              <option value="">To…</option>
              {periodOptions.map(p => <option key={p} value={p}>{displayPeriod(p)}</option>)}
            </select>
          </div>
        )}

        <select data-testid="sales-filter-month" value={fMonth} onChange={(e) => setFMonth(e.target.value)}
                className="fm-select fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13px] bg-white min-w-[140px]">
          <option value="">All months</option>
          {Array.from({length: 12}, (_, i) => String(i + 1).padStart(2, '0'))
            .map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        {hasAnyFilter && (
          <button onClick={clearFilters}
                  data-testid="sales-clear-filters"
                  className="px-2 py-2 text-[12px] text-[#7a6f5c] hover:underline">
            Clear filters
          </button>
        )}
        <div className="ml-auto text-[12px] text-[#7a6f5c]">
          {filteredItems.length} of {items.length} shown
        </div>
      </div>

      <div className="fm-card overflow-x-auto">
        {canBulkDelete && selected.size > 0 && (
          <div className="flex items-center justify-between px-4 py-2 border-b border-[#E7E2D3] bg-[#FBF6E7]">
            <div className="text-[12.5px] text-[#3d3427]">
              <b>{selected.size}</b> record{selected.size === 1 ? '' : 's'} selected
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelected(new Set())}
                className="text-[12px] text-[#7a6f5c] hover:underline"
                data-testid="sales-bulk-clear-selection"
              >Clear selection</button>
              <button
                onClick={bulkDelete}
                disabled={bulkBusy}
                data-testid="sales-bulk-delete-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-white text-[12.5px] font-medium disabled:opacity-60"
                style={{ background: '#B84A4A' }}
              >
                {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                Delete selected ({selected.size})
              </button>
            </div>
          </div>
        )}
        {loading ? <div className="p-8 text-center"><Loader2 className="animate-spin inline" /></div> : (
          <table className="w-full text-[14px] min-w-[900px]">
            <thead>
              <tr className="text-left text-[12px] text-[#7a6f5c] border-b border-[#E7E2D3]">
                {canBulkDelete && (
                  <th className="pl-5 pr-2 py-3 w-[36px]">
                    <button
                      onClick={toggleAllShown}
                      data-testid="sales-select-all"
                      className="text-[#7a6f5c] hover:text-[#232323]"
                      title="Select / clear all rows on this page"
                    >
                      {filteredItems.length > 0 && filteredItems.every(s => selected.has(s.id))
                        ? <CheckSquare size={15} style={{ color: '#4D7C5F' }} />
                        : <Square size={15} />}
                    </button>
                  </th>
                )}
                <th className="px-5 py-3 font-medium">EBO</th>
                <th className="px-5 py-3 font-medium">SKU</th>
                <th className="px-5 py-3 font-medium">Product</th>
                <th className="px-5 py-3 font-medium">Period</th>
                <th className="px-5 py-3 font-medium text-right pr-6">Qty sold</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((s, i) => (
                <tr key={s.id || i} className="fm-row border-t border-[#EFEADC]">
                  {canBulkDelete && (
                    <td className="pl-5 pr-2 py-3">
                      <button
                        onClick={() => toggleOne(s.id)}
                        data-testid={`sales-row-check-${i}`}
                        className="text-[#7a6f5c] hover:text-[#232323]"
                      >
                        {selected.has(s.id)
                          ? <CheckSquare size={15} style={{ color: '#4D7C5F' }} />
                          : <Square size={15} />}
                      </button>
                    </td>
                  )}
                  <td className="px-5 py-3">{s.ebo}</td>
                  <td className="px-5 py-3 font-mono text-[13px]">{s.sku}</td>
                  <td className="px-5 py-3">{s.product}</td>
                  <td className="px-5 py-3 font-mono text-[13px]">{displayPeriod(s.period)}</td>
                  <td className="px-5 py-3 text-right pr-6 font-semibold">{s.qty}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="fm-card p-5 w-full max-w-[520px]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-semibold text-[16px]">Add sales record</div>
              <button onClick={() => setShowModal(false)}><X size={16} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-[12.5px] font-medium">EBO Branch</label>
                <select value={draft.eboBranchCode} onChange={e => setDraft({ ...draft, eboBranchCode: e.target.value })} className="fm-select fm-focus mt-1 w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white">
                  <option value="">Select branch</option>
                  {ebos.map(e => <option key={e.code} value={e.code}>{e.name} ({e.code})</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-[12.5px] font-medium">SKU</label>
                <select value={draft.sku} onChange={e => setDraft({ ...draft, sku: e.target.value })} className="fm-select fm-focus mt-1 w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white">
                  <option value="">Select SKU</option>
                  {skus.map(s => <option key={s.sku} value={s.sku}>{s.sku} — {s.product}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[12.5px] font-medium">Period (MM/YYYY)</label>
                <input value={draft.period} onChange={e => setDraft({ ...draft, period: e.target.value })} placeholder="07/2026" className="fm-focus mt-1 w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white font-mono" />
              </div>
              <div>
                <label className="text-[12.5px] font-medium">Qty sold</label>
                <input type="number" value={draft.qty} onChange={e => setDraft({ ...draft, qty: e.target.value })} className="fm-focus mt-1 w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowModal(false)} className="px-3 py-2 border border-[#E7E2D3] rounded-md text-[13.5px]">Cancel</button>
              <button onClick={addNew} className="px-4 py-2 rounded-md text-white text-[13.5px] font-medium" style={{ background: theme.primary }}>Add record</button>
            </div>
          </div>
        </div>
      )}

      <ImportProgressModal
        open={importState.open}
        onClose={() => setImportState(s => ({ ...s, open: false }))}
        title="Sales Data import"
        phase={importState.phase}
        progress={importState.progress}
        fileName={importState.fileName}
        result={importState.result}
        errorMsg={importState.errorMsg}
      />
    </div>
  );
}

/** iter43 — Compact multi-select for canonical periods. Renders a
 *  "N selected" pill that opens a scrollable checkbox list. Values are
 *  canonical `YYYY-MM`; labels are `MM/YYYY` via the `display` prop. */
function PeriodMultiSelect({ options, selected, onToggle, onClear, display }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const count = selected.size;
  return (
    <div className="relative" ref={ref} data-testid="sales-period-multi">
      <button
        onClick={() => setOpen(v => !v)}
        data-testid="sales-period-multi-trigger"
        className="fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13px] bg-white min-w-[160px] text-left flex items-center gap-2"
      >
        <span className="truncate">
          {count === 0 ? 'All periods'
            : count === 1 ? display(Array.from(selected)[0])
            : `${count} periods selected`}
        </span>
        <span className="ml-auto text-[#7a6f5c]">▾</span>
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-[220px] fm-card p-2 max-h-[280px] overflow-y-auto" data-testid="sales-period-multi-menu">
          <div className="flex items-center justify-between px-2 py-1 border-b border-[#EFEADC] mb-1">
            <span className="text-[11px] text-[#7a6f5c]">Select periods</span>
            <button onClick={onClear} className="text-[11px] text-[#7a6f5c] hover:underline" data-testid="sales-period-multi-clear">Clear</button>
          </div>
          {options.length === 0 && (
            <div className="px-2 py-2 text-[12px] text-[#7a6f5c]">No periods in data.</div>
          )}
          {options.map(p => (
            <label key={p} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-[#F7F4EA] cursor-pointer" data-testid={`sales-period-multi-opt-${p}`}>
              <input type="checkbox" checked={selected.has(p)} onChange={() => onToggle(p)} />
              <span className="font-mono text-[12.5px]">{display(p)}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
